/* This script is created and maintained by The Global Chat Team at The Estee Lauder Companies. It is part of a larger ecosystem, so please raise any issues, change or feature requests with our team directly. */

import { scriptLoadError } from "./common-utils";

export async function loadScript(url: string, callback?: () => {}): Promise<void> {
  const script = document.createElement("script");
  script.src = url;
  script.type = "text/javascript";
  script.dataset.sourceMap = url + '.map';
  script.onerror = scriptLoadError;
  if (callback) script.onload = callback;
  document.head.appendChild(script);
}

export async function loadCookieGatedScript(url: string): Promise<void> {
  const functionalCookieCategory = '3';

  if (window.OneTrust?.InsertScript) {
    window.OneTrust.InsertScript(url, 'head', null, null, functionalCookieCategory);
    return;
  }

  const timeout = setTimeout(() => {
    if (!window.OneTrust) {
      loadCookieGatedScriptFallback(url);
    }
    clearInterval(interval);
  }, 10000);

  const interval = setInterval(() => {
    if (window.OneTrust?.InsertScript) {
      window.OneTrust.InsertScript(url, 'head', null, null, '3');
      clearTimeout(timeout);
      clearInterval(interval);
    }
  }, 100)
}

function loadCookieGatedScriptFallback(url: string): void {
  const script = document.createElement('script');
  script.src = url;
  script.className = 'optanon-category-3';
  script.type = 'text/plain';
  script.onerror = scriptLoadError;
  document.head.appendChild(script);
}

export async function loadScriptMap(mapUrl: string): Promise<void> {
  const script = document.querySelector(`script[src="${mapUrl}"]`) as HTMLScriptElement;
  if (script?.dataset?.sourceMap) {
    script.dataset.sourceMap = mapUrl;
  }
}

export async function loadCss(url: string): Promise<void> {
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = url;
  link.dataset.sourceMap = url + '.map';
  document.head.appendChild(link);
}

export async function loadBrandAssets(resources: Resources, brandAssetsManifest: Manifest, bucketPath: string) {
  if (brandAssetsManifest === undefined) {
    throw new Error("LiveChat Error: Brand Asset Manifest is missing");
  }

  for (const key in resources) {
    if (resources[key].enabled) {
      await loadBrandAsset(key, brandAssetsManifest, bucketPath);
    }
  }
}

export async function loadBrandAsset(resourceKey: string, brandAssetsManifest: Manifest, bucketPath: string) {
  if (resourceKey === undefined) {
    throw new Error("Assets are missing from the manifest: ",);
  }

  const r = brandAssetsManifest[resourceKey];

  if (!r) return;

  for (const assetPath of r) {
    const url = `${bucketPath}${assetPath}`;
    if (assetPath.endsWith(".js")) {
      await loadCookieGatedScript(url);
      if (assetPath.endsWith(".map")) {
        const mapUrl = url + ".map";
        await loadScriptMap(mapUrl);
      }
    } else if (assetPath.endsWith(".css")) {
      await loadCss(url);
    }
  }
}

export async function loadLivePersonAssets(resourceArray: string[], bucketPath: string) {
  if (resourceArray === undefined) {
    throw new Error("Assets are missing from the manifest: ",);
  }
  for (const assetPath of resourceArray) {
    const url = `${bucketPath}${assetPath}`;
    if (assetPath.endsWith(".js")) {
      await loadCookieGatedScript(url);
      if (assetPath.endsWith(".map")) {
        const mapUrl = url + ".map";
        await loadScriptMap(mapUrl);
      }
    } else if (assetPath.endsWith(".css")) {
      await loadCss(url);
    }
  }
}

export async function loadCookieGatedCoreAssets(resourceArray: string[], bucketPath: string) {
  // Ignores Asset Loader, fires health check when done
  if (resourceArray === undefined) {
    throw new Error("Core Assets are missing from the manifest: ",);
  }
  const coreAssetpath = resourceArray.filter(assetPath => !assetPath.includes('asset-loader'));

  for (const assetPath of coreAssetpath) {
    const url = `${bucketPath}${assetPath}`;
    if (assetPath.endsWith(".js")) {
      await loadScript(url);
      if (assetPath.endsWith(".map")) {
        const mapUrl = url + ".map";
        await loadScriptMap(mapUrl);
      }
    }
  }
  // Ensure everything (including other potential LP scripts) have loaded before dispatching the event

  // Ignoring health check for now...

  // window.addEventListener("load", () => {
  //   window.LiveChat = window.LiveChat || {};
  //   window.LiveChat.health = window.LiveChat.health || {};
  //   window.LiveChat.health.liveperson = window.LiveChat.health.liveperson || {};

  //   window.LiveChat.health.liveperson.assetsLoaded = true;
  //   document.dispatchEvent(new Event("liveperson_assets_loaded"));
  // });
}

export async function loadChatUiAssets(resourceArray: string[], bucketPath: string) {
  if (resourceArray === undefined) {
    throw new Error("Chat UI Assets are missing from the manifest: ",);
  }
  for (const assetPath of resourceArray) {
    const url = `${bucketPath}${assetPath}`;
    if (assetPath.endsWith(".js")) {
      await loadScript(url);
      if (assetPath.endsWith(".map")) {
        const mapUrl = url + ".map";
        await loadScriptMap(mapUrl);
      }
    } else if (assetPath.endsWith(".css")) {
      await loadCss(url);
    }
  }
}

// Called last in the asset loader pipeline
export function performLocalSetup() {
  console.log("LIVECHAT >> Local system initializing...");
  setupLocalLivePerson();
}

function setupLocalLivePerson() {
  console.log("LIVECHAT >> Setting up local LivePerson environment...");
  // Set up the minimum mock necessary for the LiveChat UI to work with a locally-mocked LivePerson Engagement
  const beautyDrawer = document.getElementById("livechat_lp_drawer_beauty");
  if (!beautyDrawer) throw new Error("Beauty Drawer not found");
  const beautyEngagement = beautyDrawer.appendChild(document.createElement("div")).appendChild(document.createElement("div"));
  beautyEngagement.setAttribute('data-lp-engagement-id', '1234567890');

  const customerSupportDrawer = document.getElementById("livechat_lp_drawer_customer_support");
  if (!customerSupportDrawer) throw new Error("Customer Support Drawer not found");
  const customerSupportEngagement = customerSupportDrawer.appendChild(document.createElement("div")).appendChild(document.createElement("div"));
  customerSupportEngagement.setAttribute('data-lp-engagement-id', '0987654321');

  window.lpTag = window.lpTag || {
    taglets: {
      rendererStub: {
        click: (engagementId: string) => {
          if (!document.querySelector(`[data-lp-engagement-id="${engagementId}"]`)) return false;
          renderMockLpWindow();
          return true;
        }
      }
    }
  };
}

function renderMockLpWindow() {
  const mockLpWindow = document.createElement("div");
  mockLpWindow.id = "lp-window-mock";
  mockLpWindow.setAttribute("style", "position: sticky; bottom: 0; left: 0; width: 350px; height: 500px; background-color: white; z-index: 9999; padding: 10px; border: 1px solid black;");
  const closeButton = document.createElement("button");
  closeButton.textContent = "X";
  closeButton.setAttribute("style", "font: bold; position: absolute; top: 0; right: 0; padding: 5px; font-size: 1.5rem;");
  closeButton.onclick = () => mockLpWindow.remove();
  mockLpWindow.appendChild(closeButton);
  mockLpWindow.appendChild(document.createElement("h2")).textContent = "Mock LivePerson Window";

  document.body.appendChild(mockLpWindow);
}
